import { useCallback, useState } from 'react';
import { poidsLisible } from '../../shared/format/poids';
import { lireLErreur } from '../../shared/api/client';
import {
  PROPORTIONS_DE_LA_PAGE,
  type CommandesDuTableau,
  type PlacementSurLaGrille,
} from './moteur';
import { useDeposerUneImage, type OrigineDuDepot } from './tableau';
import { fichierDepuisUneAdresse } from './contenu';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **F-24 — CE QUI ENTRE SUR LE TABLEAU DEPUIS L'EXTÉRIEUR, ET PAR QUELS
 * CHEMINS.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « **Sept chemins d'entrée d'une image** (Q356) : bouton d'import, collage,
 * glisser-déposer, photo prise sur le moment, fichier pris dans son espace de fichiers,
 * adresse web collée, capture d'une partie du tableau. »
 *
 * **SIX SONT SERVIS ICI. UN SEUL NE L'EST PAS, ET IL EST NOMMÉ AVEC CE QUI LUI MANQUE.**
 * Écrit ici plutôt que découvert : un chemin manquant qui n'est nommé nulle part se lit
 * comme un oubli, et personne ne va chercher sa cause.
 *
 * | Chemin | Servi | Ce qui manque |
 * |---|---|---|
 * | bouton d'import | **oui** | — |
 * | collage | **oui** | — |
 * | glisser-déposer | **oui** | — |
 * | photo prise sur le moment | **oui** | — |
 * | **fichier pris dans son espace** | **oui, depuis D-130** | — le port de contenu lit les octets de son adresse signée, et le tableau en garde une **copie** |
 * | **adresse web collée** | **oui, depuis D-130** | — le port de contenu les lit **depuis le navigateur**, jamais depuis le produit (Q360) |
 * | **capture d'une partie du tableau** | **NON, ET IL RESTE DÛ** | il faut **exporter une région choisie à la souris** : `CommandesDuTableau` n'expose ni la sélection d'une région, ni son export en image. C'est **une opération de plus au port du moteur**, donc une décision qui touche D-012 et sa sonde — pas un câblage |
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LES DEUX CHEMINS DÉBLOQUÉS DEMANDAIENT LA MÊME CHOSE, ET C'EST D-130 QUI L'A
 * TRANCHÉE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Les deux demandent d'**aller chercher des octets à une adresse depuis le navigateur**,
 * et `web/e2e/port-du-canal.spec.ts` interdisait tout `fetch` dans `web/src`. **Ce
 * n'était pas une décision d'implémentation** — elle touchait une sonde d'architecture —,
 * elle a été remontée, et le porteur a tranché : **un quatrième port**, `contenu.ts`,
 * « un fichier unique autorisé à connaître un moyen, et une sonde qui exige qu'il reste
 * unique ». Ce module ne connaît donc toujours pas ce moyen : il appelle le port.
 *
 * **Et le corpus dit lui-même que le chemin par adresse est marginal** (Q360) : « en
 * pratique le chemin par adresse devient marginal, parce que **coller une image copiée
 * depuis une page web passe par le collage**, chemin déjà retenu en Q356, et non par
 * l'adresse ». Ce chemin sert donc le cas où la personne n'a que l'adresse.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE CE MODULE NE FAIT JAMAIS, ET C'EST UNE FRONTIÈRE DE SÉCURITÉ.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **Il ne fait suivre aucune adresse au serveur.** F-24 critère 5 : « le système doit
 * **ne jamais émettre d'appel sortant vers une adresse fournie par une personne** », et
 * Q360 : « le navigateur de la personne va chercher l'image à l'adresse collée, **jamais
 * le produit** ». Ce que la route reçoit reste **des octets**, par les six chemins,
 * exactement comme le contrat le décrit — et une adresse qui ne rend rien **ne se pose
 * pas en texte sur le tableau** (Q361).
 *
 * **ET UN FICHIER PRIS DANS SON ESPACE EST COPIÉ, JAMAIS LIÉ** (Q356) : « un lien ferait
 * dépendre un tableau figé d'un fichier que son propriétaire peut supprimer ». `CDC-3`
 * F-34 le dit de l'autre côté — « **un fichier pris pour être posé sur un tableau y est
 * réduit, et l'exemplaire de l'espace reste intact** » (Q455). Ce module envoie donc les
 * octets par la même route que les autres chemins, et **rien ne retient l'identifiant du
 * fichier d'origine.**
 */

/** Ce que l'écran obtient de ce module. */
export type DepotDImages = {
  /** Le bouton d'import et la photo prise sur le moment. */
  readonly deposerDesFichiers: (
    fichiers: readonly File[],
    origine: OrigineDuDepot,
  ) => void;
  /** Le collage au clavier ou au clic droit. */
  readonly surCollage: (evenement: React.ClipboardEvent) => void;
  /** Le glisser-déposer sur la surface. */
  readonly surDepose: (evenement: React.DragEvent) => void;
  readonly surSurvol: (evenement: React.DragEvent) => void;
  /**
   * **Le fichier pris dans son espace de fichiers** — Q356, cinquième chemin.
   *
   * L'écran donne l'adresse **signée** rendue par le contrat et le nom du fichier ; ce
   * module en lit les octets par le port de contenu et les dépose. **Rien ne relie le
   * tableau au fichier d'origine** : c'est une copie (Q356, Q455).
   */
  readonly deposerUnFichierDeSonEspace: (adresse: string, nom: string) => void;
  /**
   * **L'adresse web collée** — Q356, sixième chemin, et Q360 pour qui va chercher.
   *
   * **Sur échec, rien n'est posé et le refus se dit** (Q361), dans `refus` comme tous
   * les autres.
   */
  readonly deposerUneAdresseWeb: (adresse: string) => void;
  readonly enCours: boolean;
  /** Le refus du serveur, en une phrase qui porte ce que le corpus exige. */
  readonly refus: string | null;
  readonly oublierLeRefus: () => void;
};

/**
 * **Les proportions d'un fichier, lues chez la personne et jamais devinées.**
 *
 * **Un PDF n'en a pas de lisible sans rasteriseur**, et le produit n'en embarque aucun :
 * il prend donc les proportions d'une page A4 debout, qui est le format unique du tableau
 * (Q335) et la mise à l'échelle que F-24 décrit — « une page non A4 est mise à
 * l'échelle ».
 */
async function proportionsDe(
  fichier: File,
): Promise<{ largeur: number; hauteur: number }> {
  // **Les proportions viennent du port, jamais d'ici** : la grille est « une constante
  // du produit », et une seconde déclaration voudrait dire qu'on la transporte.
  if (fichier.type === 'application/pdf') return { ...PROPORTIONS_DE_LA_PAGE };
  const adresse = URL.createObjectURL(fichier);
  try {
    return await new Promise((resoudre, rejeter) => {
      const balise = new Image();
      balise.onload = () =>
        resoudre({
          largeur: balise.naturalWidth || 1,
          hauteur: balise.naturalHeight || 1,
        });
      balise.onerror = () =>
        rejeter(new Error('Ce fichier ne s’affiche pas comme une image.'));
      balise.src = adresse;
    });
  } finally {
    URL.revokeObjectURL(adresse);
  }
}

/**
 * **Le refus, mis en phrase, et il porte ce que le corpus exige de lui.**
 *
 * « Le produit explique ses refus, **partout** » (`CDC-0` §8). Trois des quatre codes de
 * cette route ont une exigence écrite sur ce que leur message doit contenir :
 *
 * - `PLAFOND_TABLEAU_ATTEINT` : « le message porte **la place restante et ce que le
 *   document demande** » (Q367). Les deux nombres viennent de `details`, pas de la
 *   phrase — celle du serveur ne les porte pas.
 * - `CAMERA_OCCUPEE` : « le refus **dit le geste qui le lève** » (DD-19) — couper sa
 *   caméra —, et « le produit **ne coupe jamais la caméra tout seul** ».
 * - `TYPE_NON_AFFICHABLE` : « le produit refuse ce qui ne s'affiche pas, **en disant
 *   pourquoi** » (F-24).
 */
function phraseDuRefus(erreur: unknown): string {
  const lue = lireLErreur(erreur);
  if (lue.code === 'PLAFOND_TABLEAU_ATTEINT') {
    const place = lue.details?.placeRestante;
    const besoin = lue.details?.besoin;
    const chiffres =
      typeof place === 'number' && typeof besoin === 'number'
        ? ` Il reste ${poidsLisible(place)} sur ce tableau, et ce dépôt en demande ${poidsLisible(besoin)}.`
        : '';
    return `${lue.message}${chiffres}`;
  }
  if (lue.code === 'CAMERA_OCCUPEE') {
    return `${lue.message} Coupez votre caméra dans le panneau de visio, puis reprenez la photo. Le produit ne la coupe jamais tout seul.`;
  }
  return lue.message;
}

/**
 * **Le dépôt d'une image sur le tableau vivant.**
 *
 * **Il est inactif en relecture et sur un tableau gelé**, et les deux raisons sont
 * différentes : une scène passée « est figée, personne ne l'écrit » (Q317), et au
 * plafond « tout ajout est refusé pour TOUS les présents » (F-21). L'écran ne montre
 * donc pas le geste ; le serveur refuserait de toute façon, et D-111 a montré ce que
 * coûte un bouton qui mène à un refus.
 */
export function useDepotDImages(params: {
  readonly tableauId: string | null;
  readonly commandes: CommandesDuTableau | null;
  readonly actif: boolean;
}): DepotDImages {
  const depot = useDeposerUneImage(params.tableauId ?? '');
  const [refus, poserLeRefus] = useState<string | null>(null);
  /** Vrai pendant que le navigateur lit les octets d'une adresse — avant tout dépôt. */
  const [lecture, poserLaLecture] = useState(false);
  const { tableauId, commandes, actif } = params;

  const poserUnFichier = useCallback(
    async (fichier: File, origine: OrigineDuDepot) => {
      if (!actif || tableauId === null || commandes === null) return;
      poserLeRefus(null);
      try {
        const { largeur, hauteur } = await proportionsDe(fichier);
        const placement: PlacementSurLaGrille = commandes.placerUneImage(
          largeur,
          hauteur,
        );
        const pose = await depot.mutateAsync({
          fichier,
          origine,
          // **Le type annoncé ne décide rien côté serveur** : « le type réel est
          // contrôlé par la **signature binaire**, jamais par l'extension annoncée »
          // (Q357, R1). Il dit ce que la personne croit envoyer, et il sert au
          // découpage d'un PDF en pages — qui reste dû.
          type: fichier.type === 'application/pdf' ? 'pdf' : 'image',
          placement,
        });
        for (const posee of pose.elements) {
          await commandes.poserUneImage({
            id: posee.id,
            adresse: posee.adresse,
            positionX: placement.positionX,
            positionY: placement.positionY,
            largeur: placement.largeur,
            hauteur: placement.hauteur,
          });
        }
      } catch (erreur: unknown) {
        poserLeRefus(phraseDuRefus(erreur));
      }
    },
    [actif, commandes, depot, tableauId],
  );

  const deposerDesFichiers = useCallback(
    (fichiers: readonly File[], origine: OrigineDuDepot) => {
      // **Un dépôt à la fois, en série.** Le plafond est arbitré à chaque appel, et
      // deux envois concurrents le liraient tous deux avant que l'autre ait écrit —
      // « un dépôt qui atteint le plafond est refusé **en entier** » (Q367) perdrait
      // son sens si deux dépôts s'ajoutaient sans se voir.
      void (async () => {
        for (const fichier of fichiers) await poserUnFichier(fichier, origine);
      })();
    },
    [poserUnFichier],
  );

  const surCollage = useCallback(
    (evenement: React.ClipboardEvent) => {
      const fichiers = [...evenement.clipboardData.files];
      if (fichiers.length === 0) return;
      evenement.preventDefault();
      deposerDesFichiers(fichiers, 'collage');
    },
    [deposerDesFichiers],
  );

  const surDepose = useCallback(
    (evenement: React.DragEvent) => {
      const fichiers = [...evenement.dataTransfer.files];
      if (fichiers.length === 0) return;
      evenement.preventDefault();
      deposerDesFichiers(fichiers, 'glisser');
    },
    [deposerDesFichiers],
  );

  // Sans cette ligne, le navigateur ouvre le fichier à la place de la page.
  const surSurvol = useCallback((evenement: React.DragEvent) => {
    if (evenement.dataTransfer.types.includes('Files')) {
      evenement.preventDefault();
    }
  }, []);

  /**
   * **Les deux chemins qui passent par une adresse, et ils n'en font qu'un ici.**
   *
   * Une fois les octets en main, **plus rien ne distingue ces chemins des quatre
   * autres** : proportions lues chez la personne, placement demandé au moteur, dépôt par
   * la route. C'est exactement ce que Q360 promettait — « l'image rentre par le chemin
   * déjà spécifié, donc **aucune règle nouvelle** ».
   *
   * **Seule l'origine change**, et elle part au serveur telle quelle : le contrat
   * distingue déjà `espace` et `adresse` dans `OrigineDuDepot`.
   *
   * **Un échec de lecture ne pose rien** (Q361) : le `File` n'existe pas, donc
   * `poserUnFichier` n'est jamais appelé, et **aucune adresse n'est écrite en texte sur
   * le tableau** — le produit n'a de toute façon aucune notion d'objet lien.
   */
  const deposerDepuisUneAdresse = useCallback(
    (adresse: string, nom: string | undefined, origine: OrigineDuDepot) => {
      if (!actif) return;
      poserLeRefus(null);
      poserLaLecture(true);
      void (async () => {
        try {
          const fichier = await fichierDepuisUneAdresse(adresse, nom);
          await poserUnFichier(fichier, origine);
        } catch (erreur: unknown) {
          poserLeRefus(phraseDuRefus(erreur));
        } finally {
          poserLaLecture(false);
        }
      })();
    },
    [actif, poserUnFichier],
  );

  const deposerUnFichierDeSonEspace = useCallback(
    (adresse: string, nom: string) =>
      deposerDepuisUneAdresse(adresse, nom, 'espace'),
    [deposerDepuisUneAdresse],
  );

  const deposerUneAdresseWeb = useCallback(
    (adresse: string) => deposerDepuisUneAdresse(adresse, undefined, 'adresse'),
    [deposerDepuisUneAdresse],
  );

  return {
    deposerDesFichiers,
    surCollage,
    surDepose,
    surSurvol,
    deposerUnFichierDeSonEspace,
    deposerUneAdresseWeb,
    // **La lecture des octets compte dans « en cours », et elle est la plus longue des
    // deux étapes** : sans elle, l'écran paraîtrait inerte entre le clic et le dépôt,
    // et la personne recommencerait le geste.
    enCours: depot.isPending || lecture,
    refus,
    oublierLeRefus: () => poserLeRefus(null),
  };
}
